/**
 * Catalog SKU PDP ↔ Skio Dynamic Box integration.
 * Gated to product-info[data-catalog-sku-dynamic-box]. Does not touch June BaB.
 * @see https://help.skio.com/docs/dynamic-box-api-guide-for-developers
 */

const SKIO_DYNAMIC_BOX_API = 'https://api.skio.com/storefront-http/get-dynamic-box';

/** Prefer product metafield custom.dynamic_box — no shared default. */
const CATALOG_DYNAMIC_BOX_ID = '';

const CATALOG_SKU_BOX_DEFAULTS = {
  tier35MinQty: 4,
  subscribeDiscount: 0.2,
  subscribeTier35Discount: 0.35,
  dynamicBoxProductId: '',
  sticksPerPack: 15,
};

class CatalogSkuDynamicBox {
  constructor(root) {
    this.root = root;
    this.config = {};
    this.boxId = null;
    this.eligibleVariantIds = null;
    this.boxLoadPromise = null;
    this.moneyFormatter = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
    });
  }

  init() {
    this.parseConfig();
    this.purchaseType = 'subscribe';

    this.form =
      document.getElementById(this.config.productFormId) ||
      this.root.querySelector('form[data-type="add-to-cart-form"]');
    this.productForm = this.root.querySelector('product-form');
    this.planPicker = this.root.querySelector('[skio-plan-picker]');
    this.quantityInput =
      this.root.querySelector(`#Quantity-${this.config.sectionId}`) ||
      this.root.querySelector('input.quantity__input[name="quantity"]');

    if (!this.config.dynamicBoxProductId) {
      console.warn(
        '[catalog-sku-dynamic-box] Missing Dynamic Box UUID. Set product metafield custom.dynamic_box. Subscribe cart discount will not apply until set.'
      );
    }

    this.ensurePropertyInputs();
    this.loadDynamicBox();
    this.bindEvents();
    this.preferDynamicBoxSellingPlan();
    this.syncBoxProperties();
    this.syncCatalogPurchaseUi();
    this.updateTierDisplayPrices();

    // Skio picker init can re-enable the 20% group — re-apply after it settles.
    requestAnimationFrame(() => {
      if (this.purchaseType === 'onetime') return;
      this.preferDynamicBoxSellingPlan();
      this.syncBoxProperties();
      this.syncCatalogPurchaseUi();
      this.updateTierDisplayPrices();
    });
    setTimeout(() => {
      if (this.purchaseType === 'onetime') return;
      this.preferDynamicBoxSellingPlan();
      this.syncBoxProperties();
      this.syncCatalogPurchaseUi();
      this.updateTierDisplayPrices();
    }, 400);

    window.addEventListener('resize', () => this.updateTierBar());
    this.setupCatalogTierSticky();
  }

  parseConfig() {
    const el = this.root.querySelector('[data-catalog-sku-dynamic-box-config]');
    if (!el) {
      this.config = { ...CATALOG_SKU_BOX_DEFAULTS };
      return;
    }
    try {
      this.config = { ...CATALOG_SKU_BOX_DEFAULTS, ...JSON.parse(el.textContent) };
    } catch (error) {
      console.error('[catalog-sku-dynamic-box] Invalid config JSON', error);
      this.config = { ...CATALOG_SKU_BOX_DEFAULTS };
    }
    this.config.dynamicBoxProductId = String(
      this.config.dynamicBoxProductId || ''
    ).trim();
    if (!this.config.dynamicBoxProductId) {
      console.warn(
        '[catalog-sku-dynamic-box] Missing Dynamic Box UUID. Set product metafield custom.dynamic_box (e.g. d8c63fed-… for Watermelon). Without it, subscribe stays at one-time price.'
      );
    }
    this.config.tier35MinQty = parseInt(this.config.tier35MinQty, 10) || 4;
    this.config.subscribeDiscount = Number(this.config.subscribeDiscount) || 0.2;
    this.config.subscribeTier35Discount = Number(this.config.subscribeTier35Discount) || 0.35;
    this.config.sticksPerPack = Math.max(
      1,
      parseInt(this.config.sticksPerPack, 10) || 0
    );
  }

  getSticksPerPack() {
    this.planPicker = this.root.querySelector('[skio-plan-picker]') || this.planPicker;

    const candidates = [
      parseInt(this.config?.sticksPerPack, 10),
      parseInt(this.getCatalogPurchaseUi()?.dataset.sticksPerPack || '', 10),
      parseInt(this.planPicker?.dataset.sticksPerPack || '', 10),
    ];

    for (const value of candidates) {
      if (Number.isFinite(value) && value >= 1) return value;
    }

    // Last resort: parse SKU / title (e.g. DH-VARIETY30, WM15).
    const product = this.getProductJson();
    const haystack = [
      product?.selected_or_first_available_variant?.sku,
      product?.variants?.[0]?.sku,
      product?.handle,
      product?.title,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (haystack.includes('variety30') || /(?:^|\D)30(?:\s*)?(?:pk|ct|pack|count)?(?:\D|$)/.test(haystack)) {
      return 30;
    }
    if (/(?:^|\D)15(?:\s*)?(?:pk|ct|pack|count)?(?:\D|$)/.test(haystack)) {
      return 15;
    }

    const trailing = haystack.match(/(?:^|[\s\-_])(\d{1,2})(?:pk|ct|pack)?(?:\s|$)/);
    if (trailing) {
      const n = parseInt(trailing[1], 10);
      if (n >= 5 && n <= 60) return n;
    }

    return 15;
  }

  isBoxUuid(value) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      String(value || '').trim()
    );
  }

  parseShopifyNumericId(value) {
    const stringValue = String(value || '');
    const gidMatch = stringValue.match(/ProductVariant\/(\d+)/);
    if (gidMatch) return gidMatch[1];
    return stringValue.replace(/\D/g, '') || null;
  }

  loadDynamicBox() {
    if (this.boxLoadPromise) return this.boxLoadPromise;

    const configuredId = String(this.config.dynamicBoxProductId || '').trim();
    if (!configuredId) {
      this.boxId = null;
      this.boxLoadPromise = Promise.resolve(null);
      return this.boxLoadPromise;
    }

    // Catalog box is configured as Skio box UUID — use directly for ATC props.
    if (this.isBoxUuid(configuredId)) {
      this.boxId = configuredId;
      this.eligibleVariantIds = null;
      this.boxLoadPromise = Promise.resolve({ boxId: this.boxId });
      this.syncBoxProperties();
      return this.boxLoadPromise;
    }

    this.boxLoadPromise = fetch(SKIO_DYNAMIC_BOX_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ productPlatformId: configuredId }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (!data?.ok || !data?.DynamicBox?.boxId) {
          const detail = data?.extensions?.detail || data?.message || 'unknown error';
          throw new Error(`Skio catalog dynamic box unavailable: ${detail}`);
        }

        this.boxId = data.DynamicBox.boxId;
        this.eligibleVariantIds = new Set();

        (data.DynamicBox.selectableProductVariants || []).forEach((product) => {
          (product.productVariants || []).forEach((variant) => {
            const numericId = this.parseShopifyNumericId(variant.platformId);
            if (numericId) this.eligibleVariantIds.add(numericId);
          });
        });

        this.syncBoxProperties();
        return data.DynamicBox;
      })
      .catch((error) => {
        console.error(error);
        this.boxId = null;
        return null;
      });

    return this.boxLoadPromise;
  }

  async ensureReady() {
    await this.loadDynamicBox();
    return Boolean(this.boxId);
  }

  getSellingPlanInput() {
    return (
      this.root.querySelector('input[name="selling_plan"]') ||
      this.form?.querySelector('input[name="selling_plan"]')
    );
  }

  isSubscribeSelected() {
    if (this.purchaseType === 'onetime') return false;
    if (this.purchaseType === 'subscribe') return true;

    const onetime = this.planPicker?.querySelector('[skio-one-time]');
    if (onetime?.checked) return false;

    const sellingPlan = this.getSellingPlanInput()?.value;
    return Boolean(sellingPlan && String(sellingPlan).trim() !== '');
  }

  getQuantity() {
    const qty = parseInt(this.quantityInput?.value || '1', 10);
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  getSelectedVariantId() {
    const input =
      this.form?.querySelector('input[name="id"]') ||
      this.root.querySelector('input.product-variant-id[name="id"]');
    return String(input?.value || '').trim();
  }

  ensurePropertyInputs() {
    if (!this.form) return;

    const ensure = (name) => {
      let field = this.form.querySelector(`input[name="${name}"]`);
      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.name = name;
        this.form.appendChild(field);
      }
      return field;
    };

    this.boxIdInput = ensure('properties[_dynamicBoxId]');
    this.boxIndexInput = ensure('properties[_dynamicBoxIndex]');
    this.purchaseTypeInput = ensure('properties[_purchase_type]');
    this.sticksPerUnitInput = ensure('properties[_sticks_per_unit]');
    this.sticksPerPouchInput = ensure('properties[_sticks_per_pouch]');
    this.tierUnitsPerItemInput = ensure('properties[_tier_units_per_item]');
  }

  clearBoxProperties() {
    [this.boxIdInput, this.boxIndexInput].forEach((input) => {
      if (!input) return;
      input.value = '';
      input.disabled = true;
    });
  }

  /**
   * Line meta for June cart-guard free gift (3+ subscribe packs → free gift).
   * Each catalog qty unit = 1 pouch / tier unit.
   */
  syncLineMetaProperties() {
    if (!this.form) return;
    this.ensurePropertyInputs();

    const subscribe = this.isSubscribeSelected();
    const sticks = this.getSticksPerPack();

    if (this.purchaseTypeInput) {
      this.purchaseTypeInput.disabled = false;
      this.purchaseTypeInput.value = subscribe ? 'subscribe' : 'onetime';
    }
    if (this.sticksPerUnitInput) {
      this.sticksPerUnitInput.disabled = false;
      this.sticksPerUnitInput.value = String(sticks);
    }
    if (this.sticksPerPouchInput) {
      this.sticksPerPouchInput.disabled = false;
      this.sticksPerPouchInput.value = String(sticks);
    }
    if (this.tierUnitsPerItemInput) {
      this.tierUnitsPerItemInput.disabled = false;
      this.tierUnitsPerItemInput.value = '1';
    }
  }

  getActiveTierIndex() {
    const qty = this.getQuantity();
    if (qty >= 4) return 3;
    if (qty >= 3) return 2;
    if (qty >= 2) return 1;
    if (qty >= 1) return 0;
    return -1;
  }

  updateTierBar() {
    const markers = this.root.querySelectorAll('[data-catalog-tier-marker]');
    if (!markers.length) return;

    const tierIndex = this.isSubscribeSelected() ? this.getActiveTierIndex() : -1;
    markers.forEach((marker, i) => {
      marker.classList.toggle('is-active', i <= tierIndex);
      marker.classList.toggle('is-current', i === tierIndex);
    });

    const track = this.root.querySelector('[data-catalog-tier-track]');
    if (!track) return;

    if (tierIndex < 0) {
      track.style.setProperty('--tier-fill', '0%');
      return;
    }

    const marker = markers[tierIndex];
    if (!marker) {
      track.style.setProperty('--tier-fill', '0%');
      return;
    }

    const trackRect = track.getBoundingClientRect();
    if (!trackRect.width) {
      track.style.setProperty('--tier-fill', '0%');
      return;
    }

    const anchorRect = marker.getBoundingClientRect();
    const centerX = anchorRect.left + anchorRect.width / 2 - trackRect.left;
    const isLastTier = tierIndex >= markers.length - 1;
    const percent = isLastTier
      ? 100
      : Math.max(0, Math.min(100, (centerX / trackRect.width) * 100));
    track.style.setProperty('--tier-fill', `${percent}%`);
  }

  setupCatalogTierSticky() {
    const sticky = this.root.querySelector('[data-catalog-tier-sticky]');
    const sentinel = this.root.querySelector('[data-catalog-tier-sticky-sentinel]');
    if (!sticky || !sentinel || !('IntersectionObserver' in window)) return;

    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const syncPinnedState = (entries) => {
      if (!mobileQuery.matches) {
        sticky.classList.remove('is-pinned');
        return;
      }
      const entry = entries?.[0];
      if (!entry) return;
      sticky.classList.toggle('is-pinned', !entry.isIntersecting);
      this.updateTierBar();
    };

    this._catalogTierStickyObserver?.disconnect();
    this._catalogTierStickyObserver = new IntersectionObserver(syncPinnedState, {
      threshold: 0,
    });
    this._catalogTierStickyObserver.observe(sentinel);

    const onBreakpointChange = () => {
      if (!mobileQuery.matches) sticky.classList.remove('is-pinned');
      this.updateTierBar();
    };
    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', onBreakpointChange);
    } else {
      mobileQuery.addListener(onBreakpointChange);
    }
  }

  getProductJson() {
    try {
      const productJson = this.planPicker?.querySelector('[skio-product-json]');
      if (!productJson) return null;
      return JSON.parse(productJson.textContent);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   * Cart must use 0% "Dynamic Box Subscription" plans with the catalog box.
   * Using the separate 20% "Subscription" plan + box stacks discounts → $28.79 instead of $35.98.
   */
  preferDynamicBoxSellingPlan() {
    if (!this.planPicker) return;
    if (this.purchaseType === 'onetime') return;

    const onetime = this.planPicker.querySelector('[skio-one-time]');
    if (onetime?.checked) return;

    const product = this.getProductJson();
    if (!product?.selling_plan_groups?.length) return;

    const dynamicBoxGroup = product.selling_plan_groups.find(
      (group) => group.name === 'Dynamic Box Subscription'
    );

    // No Dynamic Box plans on this product — leave Skio groups alone (don't hide Subscription).
    if (!dynamicBoxGroup) return;

    this.planPicker.querySelectorAll('[skio-selling-plan-group]').forEach((input) => {
      const groupId = input.getAttribute('skio-selling-plan-group');
      const group = product.selling_plan_groups.find((item) => String(item.id) === String(groupId));
      const container = input.closest('[skio-group-container]');
      if (!container) return;

      if (group?.name === 'Subscription') {
        container.classList.add('hidden');
        container.style.display = 'none';
        if (input.checked) input.checked = false;
        return;
      }

      if (group?.name === 'Dynamic Box Subscription') {
        container.classList.remove('hidden');
        container.style.display = '';
        container.classList.add('skio-group-container--available');
        input.disabled = false;
      }
    });

    if (
      !this.isSubscribeSelected() &&
      !this.planPicker.querySelector('[skio-selling-plan-group]:checked')
    ) {
      return;
    }

    const boxInput = this.planPicker.querySelector(
      `[skio-selling-plan-group="${dynamicBoxGroup.id}"]`
    );
    if (!boxInput) return;

    if (!boxInput.checked) {
      boxInput.checked = true;
      boxInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const skioKey = this.planPicker.getAttribute('skio-plan-picker');
    const skio = window.SkioPlanPickers?.[skioKey];
    if (skio?.updateSellingPlan) skio.updateSellingPlan();
    else if (skio?.updateSellingPlanGroupAvailability) skio.updateSellingPlanGroupAvailability();
  }

  syncBoxProperties() {
    if (!this.form) return;

    this.syncLineMetaProperties();

    if (this.purchaseType !== 'onetime') {
      this.preferDynamicBoxSellingPlan();
    }

    if (!this.isSubscribeSelected()) {
      this.clearBoxProperties();
      return;
    }

    if (!this.boxId) {
      this.clearBoxProperties();
      return;
    }

    [this.boxIdInput, this.boxIndexInput].forEach((input) => {
      if (input) input.disabled = false;
    });

    this.boxIdInput.value = this.boxId;
    this.boxIndexInput.value = '0';
  }

  /**
   * Match Skio/Shopify "Build a Box Discount" rounding:
   * discount = floor(priceCents * rate), final = priceCents - discount
   * e.g. 4498 * 0.2 = 899.6 → floor 899 → 4498 - 899 = 3599 ($35.99)
   */
  applyShopifyPercentDiscount(priceCents, rate) {
    const discountCents = Math.floor(priceCents * rate);
    return priceCents - discountCents;
  }

  getVariantPriceCents() {
    try {
      const productJson = this.planPicker?.querySelector('[skio-product-json]');
      if (!productJson) return 0;
      const product = JSON.parse(productJson.textContent);
      const variantId = parseInt(this.getSelectedVariantId(), 10);
      const variant = product.variants?.find((item) => item.id === variantId);
      return variant?.price || product.price || 0;
    } catch (error) {
      console.error(error);
      return 0;
    }
  }

  getSubscribeDisplayRate() {
    return this.getQuantity() >= this.config.tier35MinQty
      ? this.config.subscribeTier35Discount
      : this.config.subscribeDiscount;
  }

  getCatalogPurchaseUi() {
    return this.root.querySelector('[data-catalog-sku-purchase]');
  }

  /**
   * Mirror Skio frequency options into the June-style select.
   */
  syncCatalogFrequencyOptions() {
    const ui = this.getCatalogPurchaseUi();
    const select = ui?.querySelector('[data-catalog-frequency-select]');
    if (!select || !this.planPicker) return;

    const skioSelect = this.planPicker.querySelector(
      '.skio-group-container--selected [skio-selling-plans], [skio-group-container].skio-group-container--selected [skio-selling-plans]'
    ) || this.planPicker.querySelector('[skio-selling-plans]');

    if (!skioSelect) return;

    const previous = select.value;
    select.innerHTML = '';
    Array.from(skioSelect.options).forEach((option) => {
      const next = document.createElement('option');
      next.value = option.value;
      next.textContent = option.textContent;
      select.appendChild(next);
    });

    if (previous && Array.from(select.options).some((option) => option.value === previous)) {
      select.value = previous;
    } else {
      select.value = skioSelect.value;
    }
  }

  /**
   * Keep June-style cards in sync with Skio radios / frequency.
   */
  syncCatalogPurchaseUi() {
    const ui = this.getCatalogPurchaseUi();
    if (!ui) return;

    const subscribeSelected = this.isSubscribeSelected();
    ui.querySelectorAll('[data-catalog-purchase-card]').forEach((card) => {
      const type = card.getAttribute('data-catalog-purchase-card');
      const selected = type === 'subscribe' ? subscribeSelected : !subscribeSelected;
      card.classList.toggle('is-selected', selected);
      card.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    const details = ui.querySelector('[data-catalog-subscribe-details]');
    if (details) details.hidden = !subscribeSelected;

    ui.querySelector('.pdp-june-purchase')?.classList.toggle(
      'has-subscribe-selected',
      subscribeSelected
    );

    this.syncCatalogFrequencyOptions();
  }

  selectCatalogPurchaseType(type) {
    this.planPicker = this.root.querySelector('[skio-plan-picker]') || this.planPicker;
    this.purchaseType = type === 'onetime' ? 'onetime' : 'subscribe';

    // Update UI immediately so the toggle feels instant even if Skio is slow.
    this.syncCatalogPurchaseUi();

    if (!this.planPicker) return;

    if (this.purchaseType === 'onetime') {
      this.planPicker.querySelectorAll('[skio-selling-plan-group]').forEach((input) => {
        input.checked = false;
      });
      this.planPicker.querySelectorAll('[skio-group-container]').forEach((container) => {
        container.classList.remove('skio-group-container--selected');
      });

      const onetime = this.planPicker.querySelector('[skio-one-time]');
      if (onetime) {
        onetime.checked = true;
        onetime.closest('[skio-group-container]')?.classList.add('skio-group-container--selected');
        onetime.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const sellingPlan = this.getSellingPlanInput();
      if (sellingPlan) sellingPlan.value = '';

      const skioKey = this.planPicker.getAttribute('skio-plan-picker');
      const skio = window.SkioPlanPickers?.[skioKey];
      if (skio?.updateSellingPlan) skio.updateSellingPlan();

      this.clearBoxProperties();
    } else {
      const product = this.getProductJson();
      const dynamicBoxGroup = product?.selling_plan_groups?.find(
        (group) => group.name === 'Dynamic Box Subscription'
      );
      const boxInput = dynamicBoxGroup
        ? this.planPicker.querySelector(`[skio-selling-plan-group="${dynamicBoxGroup.id}"]`)
        : this.planPicker.querySelector('[skio-selling-plan-group]:not([disabled])');

      const onetime = this.planPicker.querySelector('[skio-one-time]');
      if (onetime) onetime.checked = false;

      if (boxInput) {
        boxInput.disabled = false;
        boxInput.checked = true;
        boxInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.preferDynamicBoxSellingPlan();

      const skioKey = this.planPicker.getAttribute('skio-plan-picker');
      const skio = window.SkioPlanPickers?.[skioKey];
      if (skio?.updateSellingPlan) skio.updateSellingPlan();
    }

    this.syncBoxProperties();
    this.syncCatalogPurchaseUi();
    this.updateTierDisplayPrices();
  }

  /**
   * PDP estimate only (not applied to cart). Cart discount comes from Skio Dynamic Box.
   * Catalog UI shows unit price per pack (variant ÷ sticks), same math as June "per stick".
   */
  updateTierDisplayPrices() {
    this.planPicker = this.root.querySelector('[skio-plan-picker]') || this.planPicker;
    this.updateTierBar();

    const unitCents = this.getVariantPriceCents();
    if (!unitCents) return;

    const qty = this.getQuantity();
    const sticks = this.getSticksPerPack();
    const rate20 = this.config.subscribeDiscount;
    const rate35 = this.config.subscribeTier35Discount;
    const pack20 = this.applyShopifyPercentDiscount(unitCents, rate20);
    const pack35 = this.applyShopifyPercentDiscount(unitCents, rate35);
    const activeRate = this.getSubscribeDisplayRate();
    const discountedUnitCents = this.applyShopifyPercentDiscount(unitCents, activeRate);
    const lineCents = discountedUnitCents * qty;
    const compareCents = unitCents * qty;

    // Unit price shown in UI = discounted pack price ÷ sticks (e.g. $35.99 / 30 ≈ $1.20).
    const perPack20 = pack20 / sticks;
    const perPack35 = pack35 / sticks;
    const perPackOtp = unitCents / sticks;

    const ui = this.getCatalogPurchaseUi();
    if (ui) {
      const price20El = ui.querySelector('[data-catalog-sub-price-20]');
      const price35El = ui.querySelector('[data-catalog-sub-price-35]');
      const otpPackEl = ui.querySelector('[data-catalog-otp-pack-price]');
      const subTotal = ui.querySelector('[data-catalog-sub-total-pricing]');
      const otpTotal = ui.querySelector('[data-catalog-otp-total-pricing]');

      if (price20El) price20El.textContent = this.formatUnitMoney(perPack20);
      if (price35El) price35El.textContent = this.formatUnitMoney(perPack35);
      if (otpPackEl) otpPackEl.textContent = this.formatUnitMoney(perPackOtp);

      // Never show line totals in this block — only unit prices.
      if (subTotal) subTotal.hidden = true;
      if (otpTotal) otpTotal.hidden = true;
    }

    if (!this.planPicker || !this.isSubscribeSelected()) return;

    const perStickCents = discountedUnitCents / sticks;

    const selectedGroup = this.planPicker.querySelector(
      '.skio-group-container--selected, [skio-group-container].skio-group-container--selected'
    );
    const scope = selectedGroup || this.planPicker;

    const priceEl = scope.querySelector('[skio-subscription-price], [skio-current-price]');
    const compareEl = scope.querySelector('[skio-subscription-compare-price]');
    const perStickEl = scope.querySelector('[skio-subscription-price-per-stick]');
    const badgeEl = scope.querySelector('.skio-group-title__badge');

    if (priceEl) priceEl.textContent = this.moneyFormatter.format(lineCents / 100);
    if (compareEl) compareEl.textContent = this.moneyFormatter.format(compareCents / 100);
    if (perStickEl) perStickEl.textContent = this.formatUnitMoney(perStickCents);
    if (badgeEl) badgeEl.textContent = `save ${Math.round(activeRate * 100)}%`;
  }

  /** June-style $X.XX (avoids locale "35,99 USD"). cents may be fractional. */
  formatUnitMoney(cents) {
    const value = Number(cents) / 100;
    if (!Number.isFinite(value)) return '$0.00';
    return `$${value.toFixed(2)}`;
  }

  showError(message) {
    const wrapper =
      this.productForm?.querySelector('.product-form__error-message-wrapper') ||
      this.root.querySelector('.product-form__error-message-wrapper');
    const messageEl = wrapper?.querySelector('.product-form__error-message');
    if (!wrapper || !messageEl) {
      if (message) window.alert(message);
      return;
    }
    wrapper.toggleAttribute('hidden', !message);
    messageEl.textContent = message || '';
  }

  validateBeforeAdd() {
    if (!this.isSubscribeSelected()) return true;

    if (!this.boxId) {
      this.showError(
        'Missing Skio Dynamic Box ID. Set product metafield custom.dynamic_box to the box UUID.'
      );
      return false;
    }

    const variantId = this.getSelectedVariantId();
    if (this.eligibleVariantIds?.size && variantId && !this.eligibleVariantIds.has(variantId)) {
      this.showError('This flavor is not available for Subscribe & Save. Please choose another option.');
      return false;
    }

    return true;
  }

  bindEvents() {
    this.root.addEventListener('change', (event) => {
      const target = event.target;
      if (
        !target ||
        !(
          target.matches?.('input.quantity__input') ||
          target.matches?.('.skio-group-input') ||
          target.matches?.('[skio-selling-plans]') ||
          target.name === 'selling_plan'
        )
      ) {
        return;
      }

      // Keep our explicit OTP choice — Skio change events must not flip it back.
      if (this.purchaseType === 'onetime' && target.matches?.('[skio-selling-plan-group]')) {
        target.checked = false;
        const onetime = this.planPicker?.querySelector('[skio-one-time]');
        if (onetime) onetime.checked = true;
        const sellingPlan = this.getSellingPlanInput();
        if (sellingPlan) sellingPlan.value = '';
        this.clearBoxProperties();
        this.syncCatalogPurchaseUi();
        return;
      }

      if (target.matches?.('[skio-one-time]') && target.checked) {
        this.purchaseType = 'onetime';
      } else if (target.matches?.('[skio-selling-plan-group]') && target.checked) {
        this.purchaseType = 'subscribe';
      }

      this.planPicker = this.root.querySelector('[skio-plan-picker]');
      this.quantityInput =
        this.root.querySelector(`#Quantity-${this.config.sectionId}`) ||
        this.root.querySelector('input.quantity__input[name="quantity"]');
      this.syncBoxProperties();
      this.syncCatalogPurchaseUi();
      requestAnimationFrame(() => this.updateTierDisplayPrices());
    });

    if (this.quantityInput) {
      this.quantityInput.addEventListener('input', () => {
        this.syncBoxProperties();
        this.syncCatalogPurchaseUi();
        this.updateTierDisplayPrices();
      });
    }

    // Event delegation — survives section morph / late-rendered purchase UI.
    if (!this.root.dataset.catalogPurchaseDelegated) {
      this.root.dataset.catalogPurchaseDelegated = 'true';
      this.root.addEventListener('click', (event) => {
        const card = event.target.closest?.('[data-catalog-purchase-card]');
        if (!card || !this.root.contains(card)) return;
        if (event.target.closest('[data-catalog-frequency-select]')) return;
        event.preventDefault();
        event.stopPropagation();
        this.selectCatalogPurchaseType(card.getAttribute('data-catalog-purchase-card'));
      });
      this.root.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const card = event.target.closest?.('[data-catalog-purchase-card]');
        if (!card || !this.root.contains(card)) return;
        event.preventDefault();
        this.selectCatalogPurchaseType(card.getAttribute('data-catalog-purchase-card'));
      });
    }

    if (typeof window.SkioPlanPickerAutoInit === 'function' && !window.__catalogSkuSkioInitHooked) {
      window.__catalogSkuSkioInitHooked = true;
      const originalInit = window.SkioPlanPickerAutoInit;
      window.SkioPlanPickerAutoInit = (...args) => {
        originalInit(...args);
        document.querySelectorAll('product-info[data-catalog-sku-dynamic-box]').forEach((root) => {
          const instance = root.__catalogSkuDynamicBox;
          if (!instance || instance.purchaseType === 'onetime') return;
          instance.preferDynamicBoxSellingPlan?.();
          instance.syncBoxProperties?.();
          instance.syncCatalogPurchaseUi?.();
          instance.updateTierDisplayPrices?.();
        });
      };
    }

    if (typeof window.SkioPlanPickerUpdatePrices === 'function' && !window.__catalogSkuSkioPriceHooked) {
      window.__catalogSkuSkioPriceHooked = true;
      const originalUpdate = window.SkioPlanPickerUpdatePrices;
      window.SkioPlanPickerUpdatePrices = (...args) => {
        originalUpdate(...args);
        document.querySelectorAll('product-info[data-catalog-sku-dynamic-box]').forEach((root) => {
          const instance = root.__catalogSkuDynamicBox;
          if (!instance) return;
          instance.syncCatalogPurchaseUi?.();
          instance.updateTierDisplayPrices?.();
        });
      };
    }
    this.root.__catalogSkuDynamicBox = this;

    if (this.form) {
      this.form.addEventListener(
        'submit',
        (event) => {
          this.syncBoxProperties();
          if (this.isSubscribeSelected() && !this.validateBeforeAdd()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            if (this.productForm) {
              this.productForm.submitButton?.classList.remove('loading');
              this.productForm.submitButton?.removeAttribute('aria-disabled');
              this.productForm.querySelector('.loading__spinner')?.classList.add('hidden');
            }
          }
        },
        true
      );
    }

    const submitButton = this.productForm?.querySelector('[type="submit"]');
    if (submitButton) {
      submitButton.addEventListener(
        'click',
        async (event) => {
          if (this._resubmittingAfterBoxLoad) return;

          if (!this.isSubscribeSelected()) {
            this.clearBoxProperties();
            return;
          }

          if (this.boxId) {
            this.syncBoxProperties();
            return;
          }

          event.preventDefault();
          event.stopImmediatePropagation();
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.classList.add('loading');

          const ready = await this.ensureReady();
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');

          if (!ready) {
            this.showError(
              'Could not load subscription box. Set product metafield custom.dynamic_box to the Skio box UUID, then refresh.'
            );
            return;
          }

          this.syncBoxProperties();
          if (!this.validateBeforeAdd()) return;
          this._resubmittingAfterBoxLoad = true;
          submitButton.click();
          this._resubmittingAfterBoxLoad = false;
        },
        true
      );
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        this.form =
          document.getElementById(this.config.productFormId) ||
          this.root.querySelector('form[data-type="add-to-cart-form"]');
        this.productForm = this.root.querySelector('product-form');
        this.planPicker = this.root.querySelector('[skio-plan-picker]');
        this.ensurePropertyInputs();
        this.syncBoxProperties();
        this.syncCatalogPurchaseUi();
        requestAnimationFrame(() => this.updateTierDisplayPrices());
      });
    }
  }
}

function initCatalogSkuDynamicBoxes() {
  document.querySelectorAll('product-info[data-catalog-sku-dynamic-box]').forEach((root) => {
    if (root.dataset.catalogSkuBoxInitialized === 'true') return;
    root.dataset.catalogSkuBoxInitialized = 'true';
    const instance = new CatalogSkuDynamicBox(root);
    instance.init();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCatalogSkuDynamicBoxes);
} else {
  initCatalogSkuDynamicBoxes();
}
